import Button from '@app/components/common/Button';
import DatePicker from '@app/components/common/DatePicker';
import Dropdown from '@app/components/common/Dropdown';
import Grid from '@app/components/common/Grid';
import Modal from '@app/components/common/Modal';
import TimePicker from '@app/components/common/TimePicker/TimePicker';
import Typography from '@app/components/common/Typography';
import { dateTimeToSettingsTimeZone } from '@app/helpers/time/time';
import useRootSelector from '@app/hooks/useRootSelector';
import { selectRoles } from '@app/state/selectors/settingsSelectors';
import {
  addEmployeeShiftWeb,
  deleteEmployeeShiftWeb,
  editEmployeeShiftWeb,
} from '@app/state/settings/management/employeesWebActions';
import { useAppDispatch } from '@app/state/store';
import { selectUsers } from '@app/state/users/usersSelectors';
import { bindActionCreators } from '@reduxjs/toolkit';
import {
  actionCreatorsApp,
  DateTime,
  EMPLOYEES_SCREEN_DATE_FORMAT,
  EToastTypes,
  rolesDropdownFactory,
  TEditEmployeeShift,
} from '@westondev/tableturn-core';
import { useContext, useEffect, useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EmployeesScreenContext } from '../../../EmployeesContext';

interface IAddEditShiftModal extends WithTranslation {
  active: boolean;
  isAdd?: boolean;
  onCancel?: () => void;
  name: string;
  isEndDateRequired?: boolean;
}

const AddEditShiftModal = ({
  active,
  isAdd,
  onCancel = () => null,
  t,
  name,
  isEndDateRequired = true,
}: IAddEditShiftModal) => {
  const roles = useSelector(selectRoles);

  const dispatch = useAppDispatch();

  const { showToast } = bindActionCreators(actionCreatorsApp, dispatch);

  const { shift, setShift, fetchAllDetails } = useContext(
    EmployeesScreenContext,
  );

  const userRoles = useRootSelector(
    state => selectUsers(state)[shift?.organizationUserId || 0]?.roleIds,
  );

  const rolesDropdownOptions = useMemo(
    () =>
      rolesDropdownFactory(roles).filter(roleDropdown =>
        (userRoles || []).includes(roleDropdown.value),
      ),
    [roles, userRoles],
  );

  const handleOnChange = (
    value: number | string,
    field: keyof TEditEmployeeShift,
  ) => {
    if (!shift) {
      return;
    }

    const dateFields: (keyof TEditEmployeeShift)[] = ['startShift', 'endShift'];

    const timeFields: (keyof TEditEmployeeShift)[] = [
      'startTimeShift',
      'endTimeShift',
    ];

    if (![...dateFields, ...timeFields].includes(field)) {
      return setShift({
        ...shift,
        [field]: value,
      });
    }

    const isTime = timeFields.includes(field);

    if (isTime) {
      return setShift({
        ...shift,
        [field]: DateTime.fromISO(value as string)
          .toUTC()
          .toFormat('HH:mm'),
      });
    }

    const luxonNewDate = DateTime.fromISO(value as string).toUTC();
    setShift({
      ...shift,
      [field]: luxonNewDate.isValid
        ? luxonNewDate.toFormat(EMPLOYEES_SCREEN_DATE_FORMAT)
        : '',
    });
  };

  const handleSaveChanges = async () => {
    let formIncomplete = false;
    if (!shift?.roleId) {
      showToast({
        type: EToastTypes.ERROR,
        title: t('commonTexts.error'),
        description: t(
          'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.errors.role',
        ),
        timeout: 4000,
      });
      formIncomplete = true;
    }

    if (!shift?.startShift) {
      showToast({
        type: EToastTypes.ERROR,
        title: t('commonTexts.error'),
        description: t(
          'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.errors.startDateRequired',
        ),
        timeout: 4000,
      });
      formIncomplete = true;
    }

    if (!shift?.endShift && isEndDateRequired) {
      showToast({
        type: EToastTypes.ERROR,
        title: t('commonTexts.error'),
        description: t(
          'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.errors.endDateRequired',
        ),
        timeout: 4000,
      });
      formIncomplete = true;
    }

    const today = DateTime.fromFormat(
      dateTimeToSettingsTimeZone(
        DateTime.now().set({ second: 0, millisecond: 0 }).toISO(),
      ).toFormat(`${EMPLOYEES_SCREEN_DATE_FORMAT} HH:mm`),
      `${EMPLOYEES_SCREEN_DATE_FORMAT} HH:mm`,
    );

    const luxonStartShift = DateTime.fromFormat(
      `${shift?.startShift} ${shift?.startTimeShift}`,
      `${EMPLOYEES_SCREEN_DATE_FORMAT} HH:mm`,
    );
    const luxonEndShift = DateTime.fromFormat(
      `${shift?.endShift} ${shift?.endTimeShift}`,
      `${EMPLOYEES_SCREEN_DATE_FORMAT} HH:mm`,
    );

    if (shift?.startShift && luxonStartShift > today) {
      showToast({
        type: EToastTypes.ERROR,
        title: t('commonTexts.error'),
        description: t(
          'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.errors.futureStartDate',
        ),
        timeout: 4000,
      });
      formIncomplete = true;
    }

    if (shift?.endShift && luxonEndShift > today) {
      showToast({
        type: EToastTypes.ERROR,
        title: t('commonTexts.error'),
        description: t(
          'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.errors.futureEndDate',
        ),
        timeout: 4000,
      });
      formIncomplete = true;
    }

    if (shift?.startShift && shift?.endShift) {
      if (luxonEndShift < luxonStartShift) {
        showToast({
          type: EToastTypes.ERROR,
          title: t('commonTexts.error'),
          description: t(
            'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.errors.invalidRange',
          ),
          timeout: 4000,
        });
        formIncomplete = true;
      }
    }

    if (formIncomplete) {
      return;
    }
    let success = true;

    if (!shift) {
      return;
    }
    onCancel();

    if (isAdd) {
      success = await dispatch(addEmployeeShiftWeb(shift));
    } else {
      success = await dispatch(editEmployeeShiftWeb(shift));
    }

    if (success) {
      await fetchAllDetails();
    }
  };

  const handleDelete = async () => {
    if (!shift) {
      return;
    }
    onCancel();
    const success = await dispatch(deleteEmployeeShiftWeb(shift));

    if (success) {
      await fetchAllDetails();
    }
  };

  const startShiftDate = useMemo(
    () =>
      shift?.startShift
        ? DateTime.fromFormat(
            shift?.startShift,
            EMPLOYEES_SCREEN_DATE_FORMAT,
          ).toFormat('yyyy-MM-dd')
        : '',
    [shift?.startShift],
  );

  const endShiftDate = useMemo(
    () =>
      shift?.endShift
        ? DateTime.fromFormat(
            shift?.endShift,
            EMPLOYEES_SCREEN_DATE_FORMAT,
          ).toFormat('yyyy-MM-dd')
        : '',
    [shift?.endShift],
  );

  useEffect(() => {
    if (shift?.startShift && !shift?.startTimeShift) {
      setShift({
        ...shift,
        startTimeShift: '00:00',
      });
    }

    if (shift?.endShift && !shift?.endTimeShift) {
      setShift({
        ...shift,
        endTimeShift: '00:00',
      });
    }
  }, [shift, setShift]);

  return (
    <Modal
      size={600}
      isActive={active}
      title={
        isAdd
          ? t('managementModule.employeesScreen.employeeDetailsTable.addShift')
          : t(
              'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.title',
            )
      }
      subtitle={name}
      onModalClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t('commonButtons.cancel')}
          </Button>
          {!isAdd && (
            <Button variant="danger" onClick={handleDelete}>
              {t('commonButtons.delete')}
            </Button>
          )}
          <Button variant="primary" onClick={handleSaveChanges}>
            {t('commonButtons.saveChanges')}
          </Button>
        </>
      }>
      <Grid columnGap={20} rowGap={10}>
        <Grid.Item>
          <Dropdown
            required
            label={t('menuScreen.common.settings.roles.singular')}
            placeholder={t('commonTexts.placeholderDropdown')}
            data={[rolesDropdownOptions]}
            onChange={newRoleId => handleOnChange(newRoleId, 'roleId')}
            value={shift?.roleId || 0}
          />
        </Grid.Item>
        <Grid.Item>
          <Typography color="semanticBlue" fontWeight="medium">
            {t(
              'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.startOfShift',
            )}
          </Typography>
        </Grid.Item>
        <Grid.Item mb={6}>
          <DatePicker
            label={t('commonTexts.date')}
            required
            value={startShiftDate}
            maxValue={DateTime.now().toISODate()}
            onChange={date => handleOnChange(date, 'startShift')}
          />
        </Grid.Item>
        <Grid.Item mb={6}>
          <TimePicker
            label={t('commonTexts.time')}
            required
            utcValue={shift?.startTimeShift || '00:00'}
            maxValue={DateTime.now().toISODate()}
            onChange={date => handleOnChange(date, 'startTimeShift')}
            disabled={!shift?.startShift}
          />
        </Grid.Item>
        <Grid.Item>
          <Typography color="semanticBlue" fontWeight="medium">
            {t(
              'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.endOfShift',
            )}
          </Typography>
        </Grid.Item>
        <Grid.Item mb={6}>
          <DatePicker
            label={t('commonTexts.date')}
            required
            value={endShiftDate}
            maxValue={DateTime.now().toISODate()}
            onChange={date => handleOnChange(date, 'endShift')}
          />
        </Grid.Item>
        <Grid.Item mb={6}>
          <TimePicker
            label={t('commonTexts.time')}
            required
            disabled={!shift?.endShift}
            utcValue={shift?.endTimeShift || '00:00'}
            maxValue={DateTime.now().toISODate()}
            onChange={date => handleOnChange(date, 'endTimeShift')}
          />
        </Grid.Item>
      </Grid>
    </Modal>
  );
};

export default AddEditShiftModal;
